// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner on Quest, 2026-11-04: "The answer is just typed not read to the
 * user"): the AYNI chatbot's replies read aloud on this site, through one
 * audio output unlocked by the press that starts the turn.
 *
 * AYNI 3D's `SpeechPlayer` (copied unchanged in `src/ayni3d/`) opens a new
 * `<audio>` for every sentence. A browser that lets sound play only just after
 * a press refuses those (`NotAllowedError`, swallowed, so the reply stayed on
 * screen in silence): Chromium on Android and on the Quest is one, and a
 * reply comes seconds after the mic was pressed. So the press itself
 * (`unlock`, from the mic's own handler: the dock's or the panel's) starts a
 * Web Audio context, and every sentence plays through it. The rest is AYNI
 * 3D's way: a sentence at a time, the next fetched while one plays; when the
 * site's voice cannot read it, the device's own voice reads on (`DeviceVoice`,
 * primed by the same press); with neither, `onError` (the headset says the
 * reply was not read aloud). Without Web Audio (or with it refused), a clip
 * plays through an `<audio>` as before.
 */

import { DeviceVoice, audioMimeType, type SpeakPiece } from '../ayni3d/renderer/ui/ai-speech-io'
import { splitForSpeech } from '../ayni3d/shared/ai-speakable-text'
import type { AiChatLanguage } from '../ayni3d/shared/ai-languages'

/** Failures after which the device's own voice reads instead (AYNI 3D's list). */
const FALL_BACK_TO_DEVICE = new Set(['AI_UNAVAILABLE', 'AI_NOT_CONFIGURED', 'AI_INVALID_REQUEST', 'AI_DISABLED'])

type AudioContextLike = Pick<AudioContext, 'state' | 'resume' | 'decodeAudioData' | 'createBufferSource' | 'destination'>

export class UnlockedSpeech {
  private generation = 0
  private context: AudioContextLike | null = null
  private source: AudioBufferSourceNode | null = null
  private element: HTMLAudioElement | null = null
  private readonly deviceLanguages = new Set<AiChatLanguage>()

  constructor(
    private readonly speakPiece: SpeakPiece | null,
    private readonly onError: (code: string) => void,
    private readonly device: DeviceVoice | null = null,
    private readonly makeContext: () => AudioContextLike | null = () => (typeof AudioContext === 'undefined' ? null : new AudioContext()),
  ) {}

  /**
   * Call from the press that starts a turn (a user activation): starts the
   * audio output that every later sentence plays through, and primes the
   * device's voice.
   */
  public unlock(): void {
    try {
      this.context ??= this.makeContext()
      if (this.context && this.context.state !== 'running') void this.context.resume().catch(() => {})
    } catch {
      this.context = null
    }
    try {
      if (this.device && typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined') {
        // A silent word, said inside the press, lets the device speak later.
        const hush = new SpeechSynthesisUtterance(' ')
        hush.volume = 0
        window.speechSynthesis.speak(hush)
      }
    } catch { /* no device voice to prime */ }
  }

  /** Whether any voice can read this language. */
  public canSpeak(language: AiChatLanguage): boolean {
    return Boolean(this.speakPiece) || Boolean(this.device?.has(language))
  }

  /** Reads the reply; resolves once it has been read, cut short or failed. */
  public async say(text: string, language: AiChatLanguage): Promise<void> {
    this.stop()
    const generation = ++this.generation
    const pieces = splitForSpeech(text, 200)
    if (!pieces.length) return
    if (!this.speakPiece || this.deviceLanguages.has(language)) {
      await this.sayOnDevice(pieces, language, generation)
      return
    }
    let next: ReturnType<SpeakPiece> | null = this.speakPiece(pieces[0]!, language)
    for (let index = 0; index < pieces.length && next; index++) {
      const result = await next
      if (generation !== this.generation) return
      if (!result.ok) {
        if (FALL_BACK_TO_DEVICE.has(result.code) && this.device?.has(language)) {
          this.deviceLanguages.add(language)
          await this.sayOnDevice(pieces.slice(index), language, generation)
        } else {
          this.onError(result.code)
        }
        return
      }
      next = index + 1 < pieces.length ? this.speakPiece(pieces[index + 1]!, language) : null
      await this.play(result.audio, generation)
      if (generation !== this.generation) return
    }
  }

  public stop(): void {
    this.generation++
    this.device?.stop()
    if (this.source) {
      try { this.source.stop() } catch { /* already over */ }
      this.source = null
    }
    if (this.element) {
      this.element.pause()
      URL.revokeObjectURL(this.element.src)
      this.element = null
    }
  }

  private async sayOnDevice(pieces: string[], language: AiChatLanguage, generation: number): Promise<void> {
    if (!this.device?.has(language)) {
      this.onError('AI_NOT_CONFIGURED')
      return
    }
    for (const piece of pieces) {
      if (generation !== this.generation) return
      await this.device.say(piece, language)
    }
  }

  /** One clip: through the unlocked output when it runs, else an `<audio>`. */
  private async play(clip: Uint8Array, generation: number): Promise<void> {
    const context = this.context
    if (context && context.state === 'running') {
      try {
        const buffer = await context.decodeAudioData(clip.slice().buffer as ArrayBuffer)
        if (generation !== this.generation) return
        await new Promise<void>((resolve) => {
          const source = context.createBufferSource()
          source.buffer = buffer
          source.connect(context.destination)
          this.source = source
          source.onended = () => {
            if (this.source === source) this.source = null
            resolve()
          }
          source.start()
        })
        return
      } catch {
        // A clip it cannot decode plays the old way.
      }
    }
    await this.playElement(clip, generation)
  }

  private playElement(clip: Uint8Array, generation: number): Promise<void> {
    return new Promise((resolve) => {
      if (generation !== this.generation) {
        resolve()
        return
      }
      const audio = new Audio(URL.createObjectURL(new Blob([new Uint8Array(clip)], { type: audioMimeType(clip) })))
      this.element = audio
      const done = () => {
        URL.revokeObjectURL(audio.src)
        if (this.element === audio) this.element = null
        resolve()
      }
      audio.onended = done
      audio.onerror = done
      audio.play().catch(done)
    })
  }
}
